import React from 'react';
import PropTypes from 'prop-types';
import { LuChevronRight, LuFileDown, LuMessageSquareReply, LuUser, LuUserCheck } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import { useTranslation } from '../../../i18n/LanguageContext';
import { CARD } from '../components/ProfileUi';
import { COMPLAINT_STATUS_TONES, complaintStatusKey, complaintTypeLabel, formatLongDate } from './complaintOptions';

/** Status pill of a petition. */
export const ComplaintStatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const key = complaintStatusKey(status);
  return <Badge tone={COMPLAINT_STATUS_TONES[key]}>{t(`profilePage.complaints.status.${key}`)}</Badge>;
};

ComplaintStatusBadge.propTypes = { status: PropTypes.string };

/** Replies from the people in charge, oldest first. */
export const ComplaintReplies = ({ replies }) => {
  const { t, locale } = useTranslation();
  if (!replies || replies.length === 0) return null;
  return (
    <div className="tw-space-y-2">
      <p className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">
        <LuMessageSquareReply aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
        {t('profilePage.complaints.replies', { count: replies.length })}
      </p>
      <ul className="tw-space-y-2">
        {replies.map((reply, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={index} className="tw-rounded-xl tw-border-l-4 tw-border-teal-500 tw-bg-teal-500/[0.06] tw-px-3.5 tw-py-2.5">
            <p className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-3 tw-gap-y-0.5 tw-text-xs">
              <span className="tw-inline-flex tw-items-center tw-gap-1 tw-font-semibold tw-text-teal-800 dark:tw-text-teal-200">
                <LuUserCheck aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                {reply.repliedBy}
              </span>
              <span className="tw-text-slate-500 dark:tw-text-slate-400">{formatLongDate(reply.repliedAt, locale)}</span>
            </p>
            <p className="tw-mt-1 tw-whitespace-pre-line tw-break-words tw-text-sm tw-text-slate-700 dark:tw-text-slate-200">{reply.text}</p>
          </li>
        ))}
      </ul>
    </div>
  );
};

ComplaintReplies.propTypes = { replies: PropTypes.arrayOf(PropTypes.object) };

/**
 * One petition in a list. With `onOpen` (incoming petitions) the header opens
 * its detail and the sender is shown; the user's own petitions show the
 * text and the replies inline.
 */
const ComplaintCard = ({ complaint, onOpen }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.complaints.${key}`, vars);

  const header = (
    <>
      <div className="tw-min-w-0 tw-flex-1 tw-space-y-1">
        <p className="tw-font-mono tw-text-[11px] tw-font-semibold tw-text-slate-400 dark:tw-text-slate-500">{complaint.documentNumber || 'DLK-XXXXX'}</p>
        <p className="tw-break-words tw-text-base tw-font-semibold tw-text-slate-900 dark:tw-text-white">{complaint.subject}</p>
        {onOpen && (
          <p className="tw-flex tw-flex-wrap tw-items-center tw-gap-1 tw-text-xs tw-text-slate-600 dark:tw-text-slate-300">
            <LuUser aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
            <span className="tw-font-semibold">{complaint.employeeName}</span>
            <span className="tw-text-slate-400">({complaint.employeeEmail})</span>
          </p>
        )}
        <p className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
          <span className="tw-rounded-full tw-bg-violet-500/10 tw-px-2 tw-py-0.5 tw-font-semibold tw-text-violet-700 dark:tw-text-violet-300">{complaintTypeLabel(t, complaint)}</span>
          {complaint.targetNames?.length > 0 && <span className="tw-inline">{label('to', { names: complaint.targetNames.join(', ') })}</span>}
          <span className="tw-inline">{formatLongDate(complaint.createdAt, locale)}</span>
        </p>
      </div>
      <div className="tw-flex tw-shrink-0 tw-flex-col tw-items-end tw-gap-1.5">
        <ComplaintStatusBadge status={complaint.status} />
        {onOpen && <LuChevronRight aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-300 dark:tw-text-slate-600" />}
      </div>
    </>
  );

  return (
    <li className={`${CARD} tw-space-y-3 tw-p-4`}>
      {onOpen ? (
        <button
          type="button"
          onClick={() => onOpen(complaint)}
          aria-label={label('openDetail', { subject: complaint.subject || '' })}
          className="tw-flex tw-w-full tw-items-start tw-gap-3 tw-rounded-xl tw-bg-transparent tw-text-left focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50"
        >
          {header}
        </button>
      ) : (
        <div className="tw-flex tw-items-start tw-gap-3">{header}</div>
      )}

      {!onOpen && complaint.details && (
        <p className="tw-whitespace-pre-line tw-break-words tw-rounded-xl tw-bg-slate-500/[0.06] tw-px-3.5 tw-py-2.5 tw-text-sm tw-text-slate-700 dark:tw-text-slate-200">{complaint.details}</p>
      )}
      {!onOpen && <ComplaintReplies replies={complaint.replies} />}
      {onOpen && complaint.replies?.length > 0 && (
        <p className="tw-text-xs tw-font-medium tw-text-teal-700 dark:tw-text-teal-300">{label('replies', { count: complaint.replies.length })}</p>
      )}

      {onOpen && complaint.docxUrl && (
        <a
          href={complaint.docxUrl}
          download={complaint.docxFileName || `dilekce_${complaint.documentNumber}.docx`}
          target="_blank"
          rel="noopener noreferrer"
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-blue-500/10 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-blue-700 hover:tw-bg-blue-500/15 dark:tw-text-blue-300"
        >
          <LuFileDown aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
          {label('downloadDocx')}
        </a>
      )}
    </li>
  );
};

ComplaintCard.propTypes = {
  complaint: PropTypes.object.isRequired,
  onOpen: PropTypes.func,
};

export default ComplaintCard;
